import "./LoyaltyHistoryPopupTable.scss";
import React, { useMemo, useState } from "react";
import { Table, TableProps } from "antd";
import { ILoyaltyHistory, LoyaltyHistoryChargeTypes } from "../../../models/Loyalty";
import { useTranslation } from "react-i18next";
import { useGetLoyaltyHistoryQuery, useGetLoyaltyUserBalanceQuery } from "../../../api/queries";
import dayjs from "dayjs";

interface ILoyaltyHistoryPopupTableProps {
  userId: string;
  close: () => void;
}

type ColumnsType<T> = TableProps<T>["columns"];

const LoyaltyHistoryPopupTable: React.FC<ILoyaltyHistoryPopupTableProps> = ({ userId, close }) => {
  const { t } = useTranslation();

  const [tableParams, setTableParams] = useState<{ skip: number; pageSize: number }>({
    skip: 0,
    pageSize: 7,
  });

  const { data = { historyItemsLength: 0, historyItems: [] }, isLoading } =
    useGetLoyaltyHistoryQuery({ userId, skip: tableParams.skip, limit: tableParams.pageSize });
  const { data: balanceData = null } = useGetLoyaltyUserBalanceQuery(userId);

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.historyItemsLength;
  }, [data]);

  const handleTableChange: TableProps<ILoyaltyHistory>["onChange"] = (pagination) => {
    const current = pagination.current || 1;
    const pageSize = pagination.pageSize || 7;
    const skip = current && pageSize ? (current - 1) * pageSize : 0;
    setTableParams({
      pageSize,
      skip,
    });
  };

  const columns: ColumnsType<ILoyaltyHistory> = [
    {
      title: t("FirstName"),
      dataIndex: "firstname",
    },
    {
      title: t("LastName"),
      dataIndex: "lastname",
    },
    {
      title: t("Date"),
      render: (data: ILoyaltyHistory) => {
        return <>{dayjs(data.createdAt).format("DD/MM/YYYY")}</>;
      },
    },
    {
      title: t("Status"),
      render: (data: ILoyaltyHistory) => {
        return (
          <>
            {!data.isExpired && data.chargeType === LoyaltyHistoryChargeTypes.Receive && (
              <span className={"status earned"}>{t("Earned")}</span>
            )}
            {!data.isExpired && data.chargeType === LoyaltyHistoryChargeTypes.Deduction && (
              <span className={"status redeemed"}>{t("Redeemed")}</span>
            )}
            {data.isExpired && <span className={"status expired"}>{t("Expired")}</span>}
          </>
        );
      },
    },
    {
      title: t("PointsRedeemed"),
      render: (data: ILoyaltyHistory) => {
        return (
          <>
            {data.chargeType === LoyaltyHistoryChargeTypes.Deduction && (
              <span className={"points red"}>-{data.numberOfPoints}</span>
            )}
            {data.chargeType === LoyaltyHistoryChargeTypes.Receive && (
              <span className={"points green"}>+{data.numberOfPoints}</span>
            )}
          </>
        );
      },
    },
    {
      title: t("ExpiryDate"),
      render: (data: ILoyaltyHistory) => {
        if (data.expireDate) {
          return <>{dayjs(data.expireDate).format("DD/MM/YYYY")}</>;
        } else {
          return <>-</>;
        }
      },
    },
  ];

  return (
    <div className={"loyalty-history-popup-table"}>
      <div className={"popup"}>
        {balanceData && (
          <div className={"balance-wrapper"}>
            <h3>
              {t("TotalPoints")}: <span>{balanceData.loyaltyPointsBalance}</span>
            </h3>
            {balanceData.earliestExpirePoints && balanceData.earliestExpireDate && (
              <span className={"date"}>
                {balanceData.earliestExpirePoints} {t("PointsAreExpiringOn")}{" "}
                {dayjs(balanceData.earliestExpireDate).format("DD/MM/YYYY")}
              </span>
            )}
          </div>
        )}

        <Table
          className={"table"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data.historyItems}
          pagination={{ total: totalCount }}
          loading={isLoading}
          onChange={handleTableChange}
        />
        <button className={"close"} type={"button"} onClick={close}>
          {t("Close")}
        </button>
      </div>
    </div>
  );
};

export default LoyaltyHistoryPopupTable;
